import { useState } from 'react';
import { Icon } from '@/components/ui/Icon';
import { sound, type MusicTheme } from '@/game/audio/sound';

interface SoundSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function SoundSettingsModal({ isOpen, onClose }: SoundSettingsModalProps) {
  /* Seeded from what is actually stored, not from constants. These were fixed
     `useState` defaults, so the sliders opened at 100/85/70 no matter where the
     player had left them — the panel showed one thing and the engine did
     another. */
  const [prefs] = useState(() => sound.getPrefs());
  const [masterVol, setMasterVol] = useState(prefs.master);
  const [sfxVol, setSfxVol] = useState(prefs.sfx);
  const [bgmVol, setBgmVol] = useState(prefs.bgm);
  const [muted, setMuted] = useState(sound.isMuted());
  const [currentTheme, setCurrentTheme] = useState<MusicTheme>(
    () => sound.currentTheme() ?? 'mining',
  );

  if (!isOpen) return null;

  const handleMasterChange = (val: number) => {
    setMasterVol(val);
    sound.setVolume(val, sfxVol, bgmVol);
  };

  const handleSfxChange = (val: number) => {
    setSfxVol(val);
    sound.setVolume(masterVol, val, bgmVol);
    sound.playSfx('hit');
  };

  const handleBgmChange = (val: number) => {
    setBgmVol(val);
    sound.setVolume(masterVol, sfxVol, val);
  };

  const handleMuteToggle = () => {
    const isNowMuted = sound.toggleMute();
    setMuted(isNowMuted);
  };

  const handleSelectTheme = (theme: MusicTheme) => {
    setCurrentTheme(theme);
    sound.playTheme(theme);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-md animate-[fadeIn_.2s_ease-out] select-none"
      role="dialog"
      aria-modal="true"
      aria-label="Audio & Soundscape Settings"
    >
      <div className="w-full max-w-sm rounded-3xl border border-amber-500/30 bg-slate-900/90 p-5 backdrop-blur-xl shadow-[0_16px_48px_rgba(0,0,0,0.6)]">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-white/10 pb-3">
          <div className="flex items-center gap-2">
            <Icon name="sound" size={20} className="text-amber-400" />
            <h2 className="font-pixel text-[11px] tracking-wider text-amber-300 uppercase drop-shadow-[0_0_8px_rgba(255,180,60,0.5)]">
              AUDIO & SOUNDSCAPES
            </h2>
          </div>
          <button
            type="button"
            onClick={handleMuteToggle}
            className={`rounded-xl border px-2.5 py-1 font-pixel text-[8px] flex items-center gap-1 transition-all ${
              muted
                ? 'border-rose-500/40 bg-rose-500/20 text-rose-300'
                : 'border-amber-500/40 bg-amber-500/20 text-amber-300'
            }`}
          >
            <Icon name="sound" size={12} className={muted ? 'text-rose-300' : 'text-amber-300'} />
            <span>{muted ? 'MUTED' : 'AUDIO ON'}</span>
          </button>
        </div>

        {/* Volume Sliders */}
        <div className="mt-4 space-y-4">
          {/* Master Volume */}
          <div>
            <div className="flex justify-between text-[10px] font-pixel text-ink-bright">
              <span>MASTER VOLUME</span>
              <span className="text-amber-300">{masterVol}%</span>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              value={masterVol}
              onChange={(e) => handleMasterChange(Number(e.target.value))}
              aria-label="Master volume"
              className="mt-1.5 h-1.5 w-full cursor-pointer appearance-none rounded-lg bg-white/15 accent-amber-400"
            />
          </div>

          {/* SFX Volume */}
          <div>
            <div className="flex justify-between text-[10px] font-pixel text-ink-bright">
              <span>EFFECTS (SFX)</span>
              <span className="text-amber-300">{sfxVol}%</span>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              value={sfxVol}
              onChange={(e) => handleSfxChange(Number(e.target.value))}
              aria-label="Sound effects volume"
              className="mt-1.5 h-1.5 w-full cursor-pointer appearance-none rounded-lg bg-white/15 accent-amber-400"
            />
          </div>

          {/* Cavern Ambient Music */}
          <div>
            <div className="flex justify-between text-[10px] font-pixel text-ink-bright">
              <span>CAVERN AMBIENT BGM</span>
              <span className="text-amber-300">{bgmVol}%</span>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              value={bgmVol}
              onChange={(e) => handleBgmChange(Number(e.target.value))}
              aria-label="Cavern ambient music volume"
              className="mt-1.5 h-1.5 w-full cursor-pointer appearance-none rounded-lg bg-white/15 accent-amber-400"
            />
          </div>
        </div>

        {/* Subterranean Theme Selector */}
        <div className="mt-5 border-t border-white/10 pt-4">
          <p className="font-pixel text-[8px] text-ink-dim uppercase tracking-wider">
            SUBTERRANEAN SOUNDSCAPES
          </p>
          <div className="mt-2 grid grid-cols-2 gap-2">
            {[
              { id: 'mining', name: 'Mining 110BPM', icon: 'pick' as const },
              { id: 'deep_cave', name: 'Deep Cave', icon: 'lamp' as const },
              { id: 'crystal_cave', name: 'Crystal Cave', icon: 'sparkle' as const },
              { id: 'lava_cave', name: 'Lava Ambient', icon: 'flame' as const },
            ].map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => handleSelectTheme(t.id as MusicTheme)}
                className={`rounded-xl border px-2.5 py-1.5 font-pixel text-[7.5px] flex items-center justify-center gap-1.5 transition-all ${
                  currentTheme === t.id
                    ? 'border-amber-400 bg-amber-500/25 text-amber-200 shadow-[0_0_12px_rgba(245,158,11,0.4)]'
                    : 'border-white/15 bg-white/5 text-ink-dim hover:bg-white/10'
                }`}
              >
                <Icon name={t.icon} size={12} />
                <span>{t.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Close Button */}
        <button
          type="button"
          onClick={onClose}
          className="mt-5 w-full rounded-2xl border border-white/20 bg-white/10 py-2.5 font-pixel text-[10px] text-white hover:bg-white/20 active:scale-98 transition-all"
        >
          DONE
        </button>
      </div>
    </div>
  );
}
